'use client';

/**
 * Two independent, side-by-side rotating image banners.
 *
 * Port of ThemeMaker's {@code lms-portal CategoryAndBanners} banner row
 * (the {@code ImageBanner smallLeft} / {@code ImageBanner smallRight}
 * pair) — the category rail above it in the original ships as its own
 * block ({@code HeroCategoryBanner}'s category rail / a dedicated
 * category-slider block), not part of this one.
 *
 * Each side is its own carousel (1+ slides — a single slide renders
 * static, no controls) built on the exact slide/track/arrows/dots/
 * touch/keyboard logic {@code HeroCategoryBanner}'s {@code BannerCarousel}
 * uses, but the two panels size and fit their images differently — same
 * split as the original {@code ImageBanner.module.css} ({@code smallLeft}
 * vs {@code smallRight}):
 * <ul>
 *   <li><b>Primary (left):</b> no fixed height — adopts each image's own
 *       aspect ratio (natural-ratio probe, {@code object-fit: cover}) so
 *       nothing is ever cropped by a mismatched box, with a fluid
 *       {@code clamp()} floor seeded from the 995×400 design size for
 *       before the first image loads.</li>
 *   <li><b>Secondary (right):</b> height is locked to match the primary
 *       panel's live rendered height (measured via {@code ResizeObserver},
 *       same technique {@code HeroCategoryBanner} uses to size its
 *       category rail to the banner) with a black letterbox background
 *       and {@code object-fit: contain} — so a 493-wide image never
 *       stretches or crops to fill a taller/wider box, it just letterboxes,
 *       exactly like the production {@code smallRight} banner.</li>
 * </ul>
 */
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type TouchEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  PortalLink,
  isVisible,
  useCurrentUser,
  useBlockPart,
  type CurrentUser,
} from '@akademi/site-builder-runtime';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import type { CampaignAudience } from '../_shared/campaignAudience';
import { HeroEmptyFallback } from '../_shared/HeroEmptyFallback';
import { BlockRoot } from '../_shared/BlockRoot';

export interface HeroDualBannerSlide extends CampaignAudience {
  image?: {
    url?: string;
    /** Alt text. Leave blank for a purely decorative slide. */
    alt?: string;
  };
  href?: string;
}

export interface HeroDualBannerProps {
  /** Left / large panel. Recommended image size 995×400. One entry
   *  renders static (no carousel controls). */
  primarySlides?: HeroDualBannerSlide[];
  /** Right / small panel. Recommended image size 493×400. One entry
   *  renders static (no carousel controls). */
  secondarySlides?: HeroDualBannerSlide[];
  autoplay?: boolean;
  /** Min 2000 enforced at render. */
  autoplayDelay?: number;
  /** Default true. Ignored on a panel with < 2 slides. */
  showArrows?: boolean;
  /** Default true. Ignored on a panel with < 2 slides. */
  showDots?: boolean;
  radius?: 'none' | 'sm' | 'md' | 'lg';
  /** 'full' (default) lets the block span the section edge-to-edge.
   *  'theme' constrains it to the page's max-width container — same
   *  toggle/naming as HeroCarousel's `maxWidth`. */
  maxWidth?: 'full' | 'theme';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const RADIUS_MAP: Record<NonNullable<HeroDualBannerProps['radius']>, string> = {
  none: '0px',
  sm: 'var(--radius-sm, 6px)',
  md: 'var(--radius-md, 12px)',
  lg: 'var(--radius-lg, 20px)',
};

const SWIPE_THRESHOLD = 50;
const HORIZONTAL_LOCK_RATIO = 1.2;

export function HeroDualBanner(props: HeroDualBannerProps) {
  const {
    primarySlides,
    secondarySlides,
    autoplay = true,
    autoplayDelay = 5000,
    showArrows = true,
    showDots = true,
    radius = 'lg',
    maxWidth = 'full',
    paddingOuter,
    paddingInner,
  } = props;

  const { t } = useTranslation('site-builder-blocks');
  const outerPad = resolvePaddingStyle(paddingOuter);
  const innerPad = resolvePaddingStyle(paddingInner);
  const resolvedRadius = RADIUS_MAP[radius];

  // CSS alone can't lock the secondary panel's height to the primary's —
  // the primary sizes itself from its own image's aspect ratio (intrinsic,
  // not stretchable), so `align-items: stretch` has nothing to stretch
  // against. Measuring it directly and applying that as the secondary's
  // fixed height is the same technique HeroCategoryBanner uses to size
  // its category rail off the banner panel.
  const primaryRef = useRef<HTMLDivElement>(null);
  const [primaryHeight, setPrimaryHeight] = useState<number | null>(null);
  useEffect(() => {
    const el = primaryRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height;
      if (h) setPrimaryHeight(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <BlockRoot
      as="section"
      style={{
        ...outerPad,
        ...(maxWidth === 'theme'
          ? {
              maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
              width: '100%',
              margin: '0 auto',
            }
          : {}),
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: gridCss }} />
      <div className="hdb-grid" style={{ ...gridStyle, ...innerPad }}>
        <BannerPanel
          panelRef={primaryRef}
          fit="cover"
          slides={primarySlides}
          defaultAspectRatio={995 / 400}
          autoplay={autoplay}
          autoplayDelay={autoplayDelay}
          showArrows={showArrows}
          showDots={showDots}
          radius={resolvedRadius}
          ariaLabel={t('hero.carousel.primaryBanner')}
          onHeightChange={setPrimaryHeight}
        />
        <BannerPanel
          fit="contain"
          matchHeightPx={primaryHeight}
          slides={secondarySlides}
          defaultAspectRatio={493 / 400}
          autoplay={autoplay}
          autoplayDelay={autoplayDelay}
          showArrows={showArrows}
          showDots={showDots}
          radius={resolvedRadius}
          ariaLabel={t('hero.carousel.secondaryBanner')}
        />
      </div>
    </BlockRoot>
  );
}

// =============================================================================
// Banner panel — one self-contained carousel. Reused as-is for both the
// primary and secondary side; only the seed aspect ratio differs.
// =============================================================================

function BannerPanel({
  slides: rawSlides,
  defaultAspectRatio,
  autoplay,
  autoplayDelay,
  showArrows,
  showDots,
  radius,
  ariaLabel,
  fit,
  matchHeightPx,
  panelRef,
  onHeightChange,
}: {
  slides?: HeroDualBannerSlide[];
  defaultAspectRatio: number;
  autoplay: boolean;
  autoplayDelay: number;
  showArrows: boolean;
  showDots: boolean;
  radius: string;
  ariaLabel: string;
  /** 'cover' (primary): sizes the box from the active image's own ratio,
   *  image fills it edge-to-edge. 'contain' (secondary): box height comes
   *  from `matchHeightPx` instead, image letterboxes on a dark backdrop
   *  rather than cropping/stretching to fill. */
  fit: 'cover' | 'contain';
  /** Fixed height (px) to lock this panel to — 'contain' panels only.
   *  Null until the primary panel's first height report lands. */
  matchHeightPx?: number | null;
  /** Attached to the rendered panel root so the parent can measure its
   *  real height — 'cover'/primary panel only. */
  panelRef?: React.RefObject<HTMLDivElement | null>;
  /** 'cover'/primary panel only: fired right after `activeRatio` changes
   *  the panel's own rendered height (a new image loads with a different
   *  ratio). The parent's ResizeObserver *should* catch this on its own,
   *  but some browsers don't reliably re-fire ResizeObserver for a resize
   *  driven purely by a CSS `aspect-ratio` recalculation rather than an
   *  explicit width/height change — this closes that gap by measuring
   *  synchronously, right after the browser has committed the new layout. */
  onHeightChange?: (height: number) => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const currentUser = useCurrentUser<CurrentUser>();
  const slides = useMemo(() => {
    const list = Array.isArray(rawSlides) ? rawSlides : [];
    return list.filter((s) => s?.image?.url).filter((s) => isVisible(s, currentUser));
  }, [rawSlides, currentUser]);

  const count = slides.length;
  const isCarousel = count > 1;

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [pageHidden, setPageHidden] = useState(false);

  // Natural width/height of each slide's image — same probe as
  // HeroCategoryBanner's BannerCarousel. Anchored to slide 0 (not the
  // active slide) so autoplay ticks don't visibly resize the panel when
  // slides have different image ratios.
  const [naturalRatios, setNaturalRatios] = useState<Record<number, number>>({});
  const activeRatio = naturalRatios[0] ?? naturalRatios[index];
  const recordRatio = (i: number, img: HTMLImageElement) => {
    const { naturalWidth, naturalHeight } = img;
    if (!naturalWidth || !naturalHeight) return;
    const ratio = naturalWidth / naturalHeight;
    setNaturalRatios((prev) => (prev[i] === ratio ? prev : { ...prev, [i]: ratio }));
  };

  // See onHeightChange's doc comment — re-measures right after the browser
  // commits the aspect-ratio-driven height change, instead of trusting the
  // parent's ResizeObserver to catch it on its own.
  useLayoutEffect(() => {
    if (fit !== 'cover' || !onHeightChange || !panelRef?.current) return;
    const h = panelRef.current.getBoundingClientRect().height;
    if (h) onHeightChange(h);
  }, [fit, onHeightChange, panelRef, activeRatio]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReducedMotion(mq.matches);
    sync();
    mq.addEventListener?.('change', sync);
    return () => mq.removeEventListener?.('change', sync);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const sync = () => setPageHidden(document.hidden);
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);

  useEffect(() => {
    if (!isCarousel || !autoplay || reducedMotion || paused || pageHidden) return;
    const delay = Math.max(2000, autoplayDelay);
    const t = setTimeout(() => setIndex((i) => (i + 1) % count), delay);
    return () => clearTimeout(t);
  }, [index, isCarousel, autoplay, reducedMotion, paused, pageHidden, autoplayDelay, count]);

  const go = (next: number) => {
    if (!isCarousel) return;
    setIndex(((next % count) + count) % count);
  };
  const prev = () => go(index - 1);
  const next = () => go(index + 1);

  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: TouchEvent) => {
    if (!isCarousel) return;
    const t = e.touches[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: TouchEvent) => {
    if (!isCarousel) return;
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dy) * HORIZONTAL_LOCK_RATIO > Math.abs(dx)) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
    if (dx < 0) next();
    else prev();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (!isCarousel) return;
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        prev();
        break;
      case 'ArrowRight':
        e.preventDefault();
        next();
        break;
    }
  };

  const panelFloorStyle: CSSProperties =
    fit === 'contain'
      ? {
          ...bannerPanelContainStyle,
          height: matchHeightPx != null ? matchHeightPx : undefined,
          aspectRatio: matchHeightPx != null ? undefined : defaultAspectRatio,
          // The clamp() floor in bannerPanelContainStyle exists only for
          // before matchHeightPx lands — once it does, the primary panel's
          // real height (which can legitimately exceed 400px, e.g. a wider
          // viewport with a taller-than-995x400 image) must win outright,
          // or this cap silently overrides the explicit `height` above and
          // the two panels drift apart the moment images finish loading.
          maxHeight: matchHeightPx != null ? 'none' : undefined,
          borderRadius: radius,
        }
      : {
          ...bannerPanelStyle,
          aspectRatio: defaultAspectRatio,
          borderRadius: radius,
        };

  if (count === 0) {
    return (
      <div ref={panelRef} style={panelFloorStyle}>
        <HeroEmptyFallback style={{ position: 'absolute', inset: 0 }} />
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      role={isCarousel ? 'region' : undefined}
      aria-roledescription={isCarousel ? 'carousel' : undefined}
      aria-label={isCarousel ? ariaLabel : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        ...panelFloorStyle,
        // 'cover' (primary) only: once the active slide's image reports
        // its real dimensions, size purely from that ratio — matching
        // HeroCategoryBanner's BannerCarousel exactly, so the panel is
        // never cropped. 'contain' (secondary) panels stay locked to
        // matchHeightPx regardless of image ratio — that's the whole
        // point of the letterbox.
        ...(fit === 'cover' && activeRatio
          ? { height: 'auto', aspectRatio: activeRatio, maxHeight: 'none' }
          : {}),
      }}
      tabIndex={-1}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          width: `${count * 100}%`,
          transform: `translateX(-${index * (100 / count)}%)`,
          transition: reducedMotion ? 'none' : 'transform 480ms cubic-bezier(0.4, 0.0, 0.2, 1)',
        }}
      >
        {slides.map((slide, i) => {
          const active = i === index;
          const img = (
            <img
              src={resizeImageUrl(slide.image!.url!, '1200x0')}
              alt={slide.image?.alt || ''}
              loading={i === 0 ? 'eager' : 'lazy'}
              fetchPriority={i === 0 ? 'high' : undefined}
              decoding="async"
              onLoad={(e) => recordRatio(i, e.currentTarget)}
              ref={(el) => {
                if (el && el.complete) recordRatio(i, el);
              }}
              // 'contain' (secondary) only: letterboxes on mobile (stacked,
              // width no longer constrains height the way the desktop grid
              // split does) but switches to 'cover' at the 992px desktop
              // breakpoint via hdb-secondary-img in gridCss — an inline
              // style can't respond to a media query, so the desktop
              // override has to come from that scoped stylesheet instead.
              className={fit === 'contain' ? 'hdb-secondary-img' : undefined}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: fit,
              }}
              {...(slide.image?.alt === '' ? { role: 'presentation' } : {})}
            />
          );
          return (
            <article
              key={i}
              role={isCarousel ? 'group' : undefined}
              aria-roledescription={isCarousel ? 'slide' : undefined}
              aria-label={isCarousel ? `Slide ${i + 1} of ${count}` : undefined}
              aria-hidden={isCarousel && !active}
              inert={isCarousel && !active}
              style={{ flex: `0 0 ${100 / count}%`, position: 'relative' }}
            >
              {slide.href ? (
                <PortalLink
                  href={slide.href}
                  style={{ position: 'absolute', inset: 0 }}
                  tabIndex={isCarousel && !active ? -1 : undefined}
                >
                  {img}
                </PortalLink>
              ) : (
                img
              )}
            </article>
          );
        })}
      </div>

      {isCarousel && showArrows && (
        <>
          <PanelArrow direction="prev" onClick={prev} label={t('hero.carousel.previous')} />
          <PanelArrow direction="next" onClick={next} label={t('hero.carousel.next')} />
        </>
      )}

      {isCarousel && showDots && (
        <div
          role="tablist"
          aria-label={t('hero.carousel.selector')}
          {...part('tablist')}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 'clamp(8px, 1.5vw, 14px)',
            display: 'flex',
            justifyContent: 'center',
            gap: 'var(--space-2, 8px)',
            zIndex: 2,
          }}
        >
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={t('hero.carousel.goToSlide', { index: i + 1 })}
              onClick={() => go(i)}
              {...part('tab', i)}
              style={{
                // Hit area meets the 24x24 CSS px touch-target minimum even
                // though the visual pill (below) stays small.
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: i === index ? 26 : 24,
                height: 24,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'block',
                  width: i === index ? 22 : 8,
                  height: 8,
                  borderRadius: 'var(--radius-pill, 999px)',
                  background: i === index ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.4)',
                  transition: 'width 220ms ease, background 220ms ease',
                }}
              />
            </button>
          ))}
        </div>
      )}

      {isCarousel && (
        <div
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {t('hero.carousel.slidePosition', { index: index + 1, count })}
        </div>
      )}
    </div>
  );
}

function PanelArrow({
  direction,
  onClick,
  label,
}: {
  direction: 'prev' | 'next';
  onClick: () => void;
  label: string;
}) {
  const part = useBlockPart();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      {...part('pager', direction === 'prev' ? 0 : 1)}
      style={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        [direction === 'prev' ? 'left' : 'right']: 'clamp(6px, 1.5vw, 14px)',
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.25)',
        background: 'rgba(0,0,0,0.35)',
        color: 'rgba(255,255,255,0.95)',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
      }}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {direction === 'prev' ? (
          <polyline points="15 18 9 12 15 6" />
        ) : (
          <polyline points="9 18 15 12 9 6" />
        )}
      </svg>
    </button>
  );
}

// =============================================================================
// Styles
// =============================================================================

const gridStyle: CSSProperties = {
  display: 'grid',
  // Column split (995fr / 493fr on the desktop breakpoint) is owned by
  // the scoped <style> below — an inline value here would out-specificity
  // the media query and the layout could never switch from stacked to
  // side-by-side, same trap HeroCategoryBanner's grid avoids.
  gap: 'var(--space-4, 16px)',
};

// Pre-load floor only — shown before each panel's active slide reports
// its real intrinsic dimensions. Once it does, BannerPanel's inline
// style replaces both `aspectRatio` and `maxHeight` with the image's
// own ratio (see the `activeRatio` comment there).
const bannerPanelStyle: CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  maxHeight: 'clamp(160px, 30vw, 400px)',
  background: 'var(--color-surface-muted, #f4f4f5)',
};

// 'contain' (secondary) panel background — a letterbox backdrop for
// slides whose image ratio doesn't fill the height matched off the
// primary panel. Uses the theme's brand tint rather than the production
// banner's solid black, so the letterbox reads as on-brand instead of a
// hard color clash against the page background.
const bannerPanelContainStyle: CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  maxHeight: 'clamp(160px, 30vw, 400px)',
  background: 'var(--color-brand-50, #eff6ff)',
};

// Same breakpoint HeroCategoryBanner's grid switches on (992px) — matches
// the ThemeMaker source's `xl:` (1280px) intent closely enough while
// staying consistent with this package's one other dual-column hero.
const gridCss = `
  .hdb-grid { grid-template-columns: 1fr; }
  @media (min-width: 992px) {
    .hdb-grid { grid-template-columns: 995fr 493fr; align-items: stretch; }
    .hdb-secondary-img { object-fit: cover !important; }
  }
`;
